import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { StickyNote } from "lucide-react";

export default function InternalNotesSection({ instructor }: { instructor: any }) {
  const [notes, setNotes] = useState<any[]>([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await (supabase.from as any)("instructor_internal_notes")
      .select("*")
      .eq("instructor_id", instructor.id)
      .order("created_at", { ascending: false });
    setNotes(data ?? []);
  };
  useEffect(() => { load(); }, [instructor.id]);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    const { data: userData } = await supabase.auth.getUser();
    const { error } = await (supabase.from as any)("instructor_internal_notes").insert({
      instructor_id: instructor.id, body: body.trim(), author_id: userData?.user?.id ?? null,
    });
    setBusy(false);
    if (error) { toast({ title: "Could not save note", description: error.message, variant: "destructive" }); return; }
    setBody("");
    load();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><StickyNote className="h-4 w-4" /> Internal Notes</CardTitle>
        <CardDescription>Private admin notes. Timestamped and attributed to the author.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Prefers Mondays. Excellent with younger students." />
        <div className="flex justify-end">
          <Button size="sm" onClick={add} disabled={busy || !body.trim()}>Add note</Button>
        </div>
        {notes.length === 0 && <div className="text-xs text-muted-foreground">No notes yet.</div>}
        <ul className="divide-y">
          {notes.map((n) => (
            <li key={n.id} className="py-2">
              <div className="whitespace-pre-wrap">{n.body}</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                {new Date(n.created_at).toLocaleString()} · {n.author_id ? `Author ${String(n.author_id).slice(0, 8)}…` : "System"}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
